import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  BulkImportFilesParams,
  BulkImportHistoryItem,
} from '@/features/bulkImportsHistory/types/bulkImportsHistory.types';

import { getBulkImportFiles } from './bulkImportsHistory.thunk';

interface BulkImportsHistoryState {
  rows: BulkImportHistoryItem[];
  loading: boolean;
  error: string | null;
  query: BulkImportFilesParams;
  count: number;
}

const initialState: BulkImportsHistoryState = {
  rows: [],
  loading: false,
  error: null,
  query: {
    page: 1,
    size: 10,
    status: '',
    start_time: '',
    end_time: '',
  },
  count: 0,
};

const bulkImportsHistorySlice = createSlice({
  name: 'bulkImportsHistory',
  initialState,
  reducers: {
    setBulkImportsHistoryQuery: (
      state,
      action: PayloadAction<Partial<BulkImportFilesParams>>
    ) => {
      state.query = {
        ...state.query,
        ...action.payload,
      };
    },
    clearBulkImportsHistory: state => {
      state.rows = [];
      state.error = null;
      state.count = 0;
      state.query = initialState.query;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getBulkImportFiles.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBulkImportFiles.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.rows = action.payload.results;
        state.count = action.payload.count;
        state.query.page = action.payload.page;
        state.query.size = action.payload.size;
      })
      .addCase(getBulkImportFiles.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload ||
          action.error.message ||
          'Failed to fetch bulk import files';
        state.rows = [];
        state.count = 0;
      });
  },
});

export const { setBulkImportsHistoryQuery, clearBulkImportsHistory } =
  bulkImportsHistorySlice.actions;

export default bulkImportsHistorySlice.reducer;
